

import { Button } from "@/shared/components/Button";

import { useEffect, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useToast } from "@a2z/ui";

type ShareLinkButtonProps = {
  path: string;
  label: string;
  copiedLabel: string;
  ariaLabel: string;
  toastMessage?: string;
  className?: string;
  size?: "sm" | "md";
};

const sizeStyles: Record<NonNullable<ShareLinkButtonProps["size"]>, string> = {
  sm: "h-8 px-3 text-xs",
  md: "h-9 px-4 text-sm",
};

const iconSizes: Record<NonNullable<ShareLinkButtonProps["size"]>, string> = {
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
};

export function ShareLinkButton({
  path,
  label,
  copiedLabel,
  ariaLabel,
  toastMessage,
  className,
  size = "md",
}: ShareLinkButtonProps) {
  const t = useTranslations();
  const toast = useToast();
  const [didCopy, setDidCopy] = useState(false);

  useEffect(() => {
    if (!didCopy) {
      return;
    }
    const timer = window.setTimeout(() => setDidCopy(false), 1800);
    return () => window.clearTimeout(timer);
  }, [didCopy]);

  const copyWithExecCommand = (text: string) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const didCopyText = document.execCommand("copy");
    document.body.removeChild(textarea);
    return didCopyText;
  };

  const handleCopy = async () => {
    if (typeof window === "undefined") {
      return;
    }
    const link = path.startsWith("http")
      ? path
      : `${window.location.origin}${path}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setDidCopy(true);
        toast.success(toastMessage ?? t(keys.common.copied));
        return;
      }
    } catch {
      // Fallback below for unsupported/insecure clipboard API contexts.
    }

    if (copyWithExecCommand(link)) {
      setDidCopy(true);
      toast.success(toastMessage ?? t(keys.common.copied));
    }
  };

  return (
    <Button variant="ghost"
      type="button"
      onClick={handleCopy}
      aria-label={ariaLabel}
      leadingIcon={
        didCopy ? (
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className={iconSizes[size]}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        ) : (
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className={iconSizes[size]}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="9" y="9" width="10" height="10" rx="2" />
            <path d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1" />
          </svg>
        )
      }
      className={`inline-flex items-center gap-2 rounded-full border border-[color:var(--border-light)] font-semibold transition ${
        didCopy
          ? "border-[color:var(--success-500)] bg-[color:var(--success-100)] text-[color:var(--success-500)]"
          : "text-[color:var(--text-secondary)] hover:border-[color:var(--border-strong)]"
      } ${sizeStyles[size]} ${className ?? ""}`}
    >
      {didCopy ? copiedLabel : label}
    </Button>
  );
}
